import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Marker } from 'react-map-gl';
import secureLocalStorage from 'react-secure-storage';

const AVAILABILITY_CLASS_MAP = {
  available: 'availability-available',
  online: 'availability-available',
  busy: 'availability-busy',
  idle: 'availability-idle',
  away: 'availability-away',
  offline: 'availability-offline',
};

const parseStoredNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  try {
    const parsed = JSON.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (err) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
};

const getCachedCoords = () => {
  try {
    const lat = parseStoredNumber(secureLocalStorage.getItem('latitude'));
    const lon = parseStoredNumber(secureLocalStorage.getItem('longitude'));
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      return { lat, lon };
    }
  } catch (err) {
    return null;
  }
  return null;
};

const resolveAvailabilityClass = (availabilityState, status) => {
  const key = String(availabilityState || status || '').toLowerCase();
  return AVAILABILITY_CLASS_MAP[key] || '';
};

const FALLBACK_AVATAR =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

const makeClusterKey = (coords) =>
  `${coords[0].toFixed(5)}|${coords[1].toFixed(5)}`;

const metersPerPixel = (latitude, zoom) => {
  const latRad = (latitude * Math.PI) / 180;
  const scale = Math.pow(2, zoom);
  return (156543.03392 * Math.cos(latRad)) / scale;
};

const buildSpiderCoord = ({ center, index, count, zoom, mobileView }) => {
  if (!center || count <= 1) return center;
  const [lon, lat] = center;
  const basePx = mobileView ? 34 : 28;
  const radiusPx =
    count <= 3
      ? basePx
      : count <= 6
      ? basePx + 8
      : count <= 10
      ? basePx + 14
      : basePx + 20;
  const angle = (2 * Math.PI * index) / count - Math.PI / 2;
  const meters = metersPerPixel(lat, zoom) * radiusPx;
  const latRad = (lat * Math.PI) / 180;
  const safeCos = Math.max(Math.cos(latRad), 0.000001);
  const dx = Math.cos(angle) * meters;
  const dy = Math.sin(angle) * meters;
  const deltaLat = dy / 111320;
  const deltaLon = dx / (111320 * safeCos);
  return [lon + deltaLon, lat + deltaLat];
};

// Memoized PlayerMarker component
const PlayerMarker = React.memo(
  ({
    player,
    coordinates,
    handleSelectPlayer,
    onSelect,
    isMoving,
    availabilityClass,
    movementState,
    taskEligible,
    className = '',
  }) => {
    // console.log(`PlayerMarker: ${player.playerId} isMoving: ${isMoving}`);
    // if (player.playerId === 'JGMichelle' || player.playerId === 'joegalaxy') {
    //   console.log(`PlayerMarker: ${player.playerId} isMoving: ${isMoving}`);
    // }

    const markerCoords = Array.isArray(coordinates)
      ? coordinates
      : player?.lastLocation?.coordinates;
    if (
      !Array.isArray(markerCoords) ||
      markerCoords.length !== 2 ||
      !Number.isFinite(markerCoords[0]) ||
      !Number.isFinite(markerCoords[1])
    ) {
      return null;
    }
    const markerClassName = [
      'jg-marker',
      'jg-marker--player',
      availabilityClass,
      movementState === 'en_route' ? 'is-enroute' : '',
      taskEligible ? 'is-eligible' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Marker
        key={player.playerId}
        longitude={markerCoords[0]}
        latitude={markerCoords[1]}
        className={markerClassName}
      >
        <button
          className={[
            'marker-btn2',
            'presence-marker',
            className,
            availabilityClass,
            movementState === 'en_route' ? 'presence-enroute' : '',
            taskEligible ? 'presence-eligible' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSelectPlayer(player);
            onSelect?.();
          }}
        >
          <img
            className="presence-avatar"
            src={
              player.profilePhoto
                ? player.profilePhoto
                : FALLBACK_AVATAR
            }
            alt={player.playerId}
            title={player.playerId}
          />
          <span
            className={`presence-status ${availabilityClass}`}
            aria-hidden="true"
          />
          {/* Blinking dot for all players */}
          <span
            className={`blinking-dot ${isMoving ? 'green' : 'red'}`}
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: '2px solid white',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />
        </button>
      </Marker>
    );
  },
  (prevProps, nextProps) => {
    // if (
    //   prevProps.player.playerId === 'JGMichelle' ||
    //   prevProps.player.playerId === 'joegalaxy'
    // ) {
    //   console.log(
    //     `Memo condition: PlayerMarker: ${prevProps.player.playerId} isMoving: ${prevProps.isMoving} -> ${nextProps.isMoving}`,
    //   );
    // }
    const prevCoords = Array.isArray(prevProps.coordinates)
      ? prevProps.coordinates
      : prevProps.player?.lastLocation?.coordinates;
    const nextCoords = Array.isArray(nextProps.coordinates)
      ? nextProps.coordinates
      : nextProps.player?.lastLocation?.coordinates;
    const locationChanged =
      !Array.isArray(prevCoords) ||
      !Array.isArray(nextCoords) ||
      prevCoords[0] !== nextCoords[0] ||
      prevCoords[1] !== nextCoords[1];
    const profilePhotoChanged =
      prevProps.player.profilePhoto !== nextProps.player.profilePhoto;
    const isMovingChanged = prevProps.isMoving !== nextProps.isMoving;
    const availabilityChanged =
      prevProps.availabilityClass !== nextProps.availabilityClass;
    const movementStateChanged =
      prevProps.movementState !== nextProps.movementState;
    const eligibilityChanged =
      prevProps.taskEligible !== nextProps.taskEligible;

    return (
      !locationChanged &&
      !profilePhotoChanged &&
      !isMovingChanged &&
      !availabilityChanged &&
      !movementStateChanged &&
      !eligibilityChanged
    );
  },
);

// Memoized PlayerMarkers component
const PlayerMarkers = React.memo(
  ({
    playersWithin,
    player,
    handleSelectPlayer,
    presenceIndex,
    mobileView,
    mapZoom,
  }) => {
    const [tick, setTick] = useState(0);
    const [expandedClusterKey, setExpandedClusterKey] = useState(null);
    const currentPlayerId =
      player?.data?.player?._id || player?._id || null;
    const currentPlayerHandle =
      player?.data?.player?.playerId || player?.playerId || null;
    const friendIds = useMemo(() => {
      const raw = Array.isArray(player?.data?.player?.friends)
        ? player.data.player.friends
        : [];
      return new Set(
        raw
          .map((entry) =>
            typeof entry === 'string'
              ? entry
              : entry?._id || entry?.playerId || entry?.id,
          )
          .filter(Boolean),
      );
    }, [player?.data?.player?.friends]);

    // Local tick keeps "is moving" fresh without forcing parent re-renders.
    useEffect(() => {
      const intervalMs = mobileView ? 7000 : 4000;
      const interval = setInterval(() => {
        setTick((prev) => prev + 1);
      }, intervalMs);
      return () => clearInterval(interval);
    }, [mobileView]);

    const now = useMemo(() => Date.now(), [tick]);
    const cachedCoords = useMemo(() => getCachedCoords(), [tick]);
    const resolvedZoom = Number.isFinite(mapZoom) ? mapZoom : 13;
    const filteredPlayers = useMemo(() => {
      return Object.values(playersWithin)
        .sort((a, b) => a.jgkTokens - b.jgkTokens)
        .filter((playerWithin) => {
          const id =
            playerWithin?._id ||
            playerWithin?.id ||
            playerWithin?.mongoId ||
            null;
          const handle = playerWithin?.playerId || playerWithin?.handle || null;
          const isSelf =
            (currentPlayerId && id === currentPlayerId) ||
            (currentPlayerHandle && handle === currentPlayerHandle);
          const isFriend =
            (id && friendIds.has(id)) || (handle && friendIds.has(handle));
          return isSelf || isFriend;
        });
    }, [playersWithin, currentPlayerId, currentPlayerHandle, friendIds]);
    const playerEntries = useMemo(() => {
      return filteredPlayers
        .map((thePlayer) => {
          const entryId =
            thePlayer?._id || thePlayer?.id || thePlayer?.mongoId || null;
          const entryHandle =
            thePlayer?.playerId || thePlayer?.handle || null;
          const isSelf =
            (currentPlayerId && entryId === currentPlayerId) ||
            (currentPlayerHandle && entryHandle === currentPlayerHandle);
          const rawCoords = thePlayer?.lastLocation?.coordinates;
          let coordinates =
            Array.isArray(rawCoords) &&
            rawCoords.length === 2 &&
            Number.isFinite(rawCoords[0]) &&
            Number.isFinite(rawCoords[1])
              ? rawCoords
              : null;
          if (!coordinates && isSelf && cachedCoords) {
            coordinates = [cachedCoords.lon, cachedCoords.lat];
          }
          if (!coordinates) return null;
          const presenceEntry =
            presenceIndex?.get(thePlayer.playerId) ||
            presenceIndex?.get(thePlayer._id) ||
            null;
          const availabilityClass = resolveAvailabilityClass(
            presenceEntry?.availabilityState,
            presenceEntry?.status || presenceEntry?.presenceStatus,
          );
          const movementState = presenceEntry?.movementState || '';
          const taskEligible =
            !!presenceEntry?.taskEligibility ||
            !!presenceEntry?.taskEligible;
          const isMoving =
            thePlayer.lastMovementTime &&
            now - thePlayer.lastMovementTime < 30000;
          return {
            player: thePlayer,
            coordinates,
            availabilityClass,
            movementState,
            taskEligible,
            isMoving,
          };
        })
        .filter(Boolean);
    }, [
      filteredPlayers,
      currentPlayerId,
      currentPlayerHandle,
      cachedCoords,
      presenceIndex,
      now,
    ]);
    const clusters = useMemo(() => {
      const map = new Map();
      playerEntries.forEach((entry) => {
        const key = makeClusterKey(entry.coordinates);
        if (!map.has(key)) {
          map.set(key, {
            key,
            center: entry.coordinates,
            entries: [],
          });
        }
        map.get(key).entries.push(entry);
      });
      return Array.from(map.values());
    }, [playerEntries]);
    const toggleCluster = useCallback((key) => {
      setExpandedClusterKey((prev) => (prev === key ? null : key));
    }, []);
    const collapseClusters = useCallback(() => {
      setExpandedClusterKey(null);
    }, []);
    const expandedCluster = useMemo(() => {
      if (!expandedClusterKey) return null;
      return clusters.find((cluster) => cluster.key === expandedClusterKey) || null;
    }, [clusters, expandedClusterKey]);
    const showList =
      expandedCluster && expandedCluster.entries.length >= 6;

    useEffect(() => {
      if (!expandedClusterKey) return;
      const exists = clusters.some(
        (cluster) => cluster.key === expandedClusterKey,
      );
      if (!exists) {
        setExpandedClusterKey(null);
      }
    }, [clusters, expandedClusterKey]);

    useEffect(() => {
      if (!expandedClusterKey) return undefined;
      const handlePointer = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          target.closest('.presence-cluster') ||
          target.closest('.presence-cluster-list')
        ) {
          return;
        }
        setExpandedClusterKey(null);
      };
      document.addEventListener('pointerdown', handlePointer, true);
      return () => {
        document.removeEventListener('pointerdown', handlePointer, true);
      };
    }, [expandedClusterKey]);

    return (
      <>
        {clusters.map((cluster) => {
          if (cluster.entries.length === 1) {
            const entry = cluster.entries[0];
            return (
              <PlayerMarker
                key={entry.player.playerId}
                player={entry.player}
                coordinates={entry.coordinates}
                handleSelectPlayer={handleSelectPlayer}
                isMoving={entry.isMoving}
                availabilityClass={entry.availabilityClass}
                movementState={entry.movementState}
                taskEligible={entry.taskEligible}
              />
            );
          }
          const expanded = expandedClusterKey === cluster.key;
          const useList =
            expanded && cluster.entries.length >= 6;
          const useSpider =
            expanded && cluster.entries.length < 6;
          const stack = cluster.entries.slice(0, 3);
          const countLabel =
            cluster.entries.length > 9 ? '9+' : `${cluster.entries.length}`;
          return (
            <React.Fragment key={`cluster-${cluster.key}`}>
              <Marker
                longitude={cluster.center[0]}
                latitude={cluster.center[1]}
                className="jg-marker jg-marker--player jg-marker--cluster"
              >
                <button
                  type="button"
                  className={[
                    'presence-cluster',
                    expanded ? 'is-open' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleCluster(cluster.key);
                  }}
                  aria-expanded={expanded ? 'true' : 'false'}
                  aria-label={`${cluster.entries.length} players here`}
                >
                  <span className="presence-cluster__stack" aria-hidden="true">
                    {stack.map((entry, index) => (
                      <img
                        key={`${entry.player.playerId}-${index}`}
                        className={`presence-cluster__avatar is-${index + 1}`}
                        src={
                          entry.player.profilePhoto
                            ? entry.player.profilePhoto
                            : FALLBACK_AVATAR
                        }
                        alt=""
                      />
                    ))}
                  </span>
                  <span className="presence-cluster__count">{countLabel}</span>
                </button>
              </Marker>
              {useSpider
                ? cluster.entries.map((entry, index) => {
                    const spiderCoords = buildSpiderCoord({
                      center: cluster.center,
                      index,
                      count: cluster.entries.length,
                      zoom: resolvedZoom,
                      mobileView,
                    });
                    return (
                      <PlayerMarker
                        key={`${entry.player.playerId}-spider-${cluster.key}`}
                        player={entry.player}
                        coordinates={spiderCoords}
                        handleSelectPlayer={handleSelectPlayer}
                        onSelect={collapseClusters}
                        isMoving={entry.isMoving}
                        availabilityClass={entry.availabilityClass}
                        movementState={entry.movementState}
                        taskEligible={entry.taskEligible}
                        className="presence-spider"
                      />
                    );
                  })
                : null}
            </React.Fragment>
          );
        })}
        {showList ? (
          <div className="presence-cluster-list" role="dialog">
            <div className="presence-cluster-list__header">
              <div>
                <div className="presence-cluster-list__title">Players here</div>
                <div className="presence-cluster-list__subtitle">
                  {expandedCluster.entries.length} nearby players
                </div>
              </div>
              <button
                type="button"
                className="presence-cluster-list__close"
                onClick={collapseClusters}
                aria-label="Close list"
              >
                Close
              </button>
            </div>
            <div className="presence-cluster-list__body">
              {expandedCluster.entries.map((entry) => (
                <button
                  key={`list-${entry.player.playerId}`}
                  type="button"
                  className="presence-cluster-list__item"
                  onClick={() => {
                    handleSelectPlayer(entry.player);
                    collapseClusters();
                  }}
                >
                  <img
                    className="presence-cluster-list__avatar"
                    src={
                      entry.player.profilePhoto
                        ? entry.player.profilePhoto
                        : FALLBACK_AVATAR
                    }
                    alt=""
                  />
                  <div className="presence-cluster-list__text">
                    <div className="presence-cluster-list__name">
                      {entry.player.playerId || 'Local player'}
                    </div>
                    <div className="presence-cluster-list__meta">
                      Tap to view
                    </div>
                  </div>
                  <span
                    className={[
                      'presence-cluster-list__status',
                      entry.availabilityClass,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </>
    );
  },
);

export default PlayerMarkers;
